// Mode-agnostic chat UI: message list + streaming bubble + model picker + input form.
// Purely presentational — all state (messages, streaming text, model list) comes from
// useChatStream and is passed in as props, so both DataChatPanel (datatalk) and
// LeitlinienChatPanel (RAG) render through this one shell. `renderMessageExtras` lets a
// caller attach per-message extras (e.g. LeitlinienChatPanel's source-document chips)
// without ChatShell needing to know what "extras" means for any particular mode.
import { FormEvent, ReactNode } from "react"
import MarkdownMessage from "../../../MarkdownMessage"
import { ChatEntry } from "./useChatStream"

type Props<TExtras> = {
    messages: ChatEntry<TExtras>[]
    streaming: string
    loading: boolean
    input: string
    onInputChange: (value: string) => void
    onSubmit: (e: FormEvent) => void
    availableModels: string[]
    selectedModel: string
    onModelChange: (model: string) => void
    emptyHint: string
    placeholder: string
    inputDisabled?: boolean
    submitDisabledTitle?: string
    renderMessageExtras?: (extras: TExtras) => ReactNode
    footer?: ReactNode
}

export default function ChatShell<TExtras>({
    messages,
    streaming,
    loading,
    input,
    onInputChange,
    onSubmit,
    availableModels,
    selectedModel,
    onModelChange,
    emptyHint,
    placeholder,
    inputDisabled = false,
    submitDisabledTitle,
    renderMessageExtras,
    footer,
}: Props<TExtras>) {
    const disabled = loading || inputDisabled || !selectedModel

    return (
        <>
            <div className="flex-1 overflow-y-auto px-4 py-3">
                {messages.length === 0 && !streaming && (
                    <p className="text-center text-xs text-gray-400 mt-4">{emptyHint}</p>
                )}
                {messages.map((msg, i) => (
                    <div
                        key={i}
                        className={`flex mb-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                        <div
                            className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                                msg.role === "user"
                                    ? "bg-blue-500 text-white"
                                    : "bg-blue-100 text-blue-800 rounded-bl-sm"
                            }`}
                        >
                            {msg.role === "assistant" ? (
                                <MarkdownMessage content={msg.content} />
                            ) : (
                                <p className="whitespace-pre-wrap">{msg.content}</p>
                            )}
                            {msg.role === "assistant" &&
                                msg.extras !== undefined &&
                                renderMessageExtras?.(msg.extras)}
                        </div>
                    </div>
                ))}
                {streaming && (
                    <div className="flex mb-3 justify-start">
                        <div className="max-w-[85%] px-3 py-2 rounded-lg rounded-bl-sm text-sm bg-blue-100 text-blue-800">
                            <MarkdownMessage content={streaming} />
                        </div>
                    </div>
                )}
                {loading && !streaming && (
                    <div className="flex mb-3 justify-start">
                        <div className="px-3 py-2 rounded-lg rounded-bl-sm text-sm bg-blue-100 text-blue-600">
                            …
                        </div>
                    </div>
                )}
            </div>

            <form
                onSubmit={onSubmit}
                className="flex gap-2 border-t border-gray-100 px-4 py-3 flex-shrink-0"
            >
                {availableModels.length > 0 && (
                    <select
                        value={selectedModel}
                        onChange={e => onModelChange(e.target.value)}
                        className="text-xs border border-gray-200 rounded px-2 pr-6 py-1 text-gray-600 bg-white focus:outline-none flex-shrink-0"
                    >
                        {availableModels.map(m => (
                            <option key={m} value={m}>
                                {m.startsWith("openai:")
                                    ? `OpenAI: ${m.slice(7)}`
                                    : `Ollama: ${m}`}
                            </option>
                        ))}
                    </select>
                )}
                <input
                    value={input}
                    onChange={e => onInputChange(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <button
                    type="submit"
                    disabled={disabled || !input.trim()}
                    title={inputDisabled ? submitDisabledTitle : undefined}
                    aria-label="Nachricht senden"
                    className={`rounded px-4 py-2 text-sm text-white ${
                        inputDisabled
                            ? "bg-gray-300 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700 disabled:opacity-40"
                    }`}
                >
                    →
                </button>
            </form>

            {footer}
        </>
    )
}
